import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Link, useSearchParams } from 'react-router-dom'
import {
  acceptOAuthConsent,
  getOAuthConsentRequest,
  type OAuthConsentRequestResponse,
  rejectOAuthConsent,
} from '../api/mcp-oauth'
import BrandLogo from '../components/BrandLogo'
import { useAuth } from '../hooks/useAuth'

/**
 * Hydra consent page for MCP OAuth (issue #112).
 *
 * Reads the consent challenge (`GET /oauth2/consent/request`) so the user
 * sees exactly which client, scopes, and audience they are approving, then
 * accepts (mirrors the grant locally, follows Hydra's `redirect_to`) or
 * rejects (explicit `access_denied`, never a token, no local row).
 */
export default function OAuth2ConsentPage() {
  const [searchParams] = useSearchParams()
  const challenge = searchParams.get('consent_challenge') ?? ''
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth()
  const [consent, setConsent] = useState<OAuthConsentRequestResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isWorking, setIsWorking] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (isAuthLoading || !challenge) {
      if (!challenge) setIsLoading(false)
      return
    }
    if (!isAuthenticated) {
      setIsLoading(false)
      return
    }
    let cancelled = false
    setIsLoading(true)
    setError('')
    getOAuthConsentRequest(challenge)
      .then((data) => {
        if (!cancelled) setConsent(data)
      })
      .catch((err) => {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Could not load consent request'
          setError(message)
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [isAuthLoading, isAuthenticated, challenge])

  const followRedirect = (redirectTo: string) => {
    window.location.href = redirectTo
  }

  const handleAccept = async () => {
    if (!challenge || isWorking) return
    setIsWorking(true)
    setError('')
    try {
      const { redirect_to } = await acceptOAuthConsent(challenge)
      toast.success('Access granted')
      followRedirect(redirect_to)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not grant access'
      setError(message)
      toast.error(message)
      setIsWorking(false)
    }
  }

  const handleReject = async () => {
    if (!challenge || isWorking) return
    setIsWorking(true)
    setError('')
    try {
      const { redirect_to } = await rejectOAuthConsent(challenge)
      toast.success('Access denied')
      followRedirect(redirect_to)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not deny access'
      setError(message)
      toast.error(message)
      setIsWorking(false)
    }
  }

  if (!challenge) {
    return (
      <div className="min-h-screen app-sky flex flex-col items-center justify-center gap-4 px-4">
        <BrandLogo size={48} src="/icon-256.png" className="rounded-2xl" />
        <h1 className="text-xl font-bold text-harbor-text">Authorize access</h1>
        <p className="max-w-md text-center text-sm text-harbor-text-secondary">
          This page approves MCP access, but it was opened without a consent challenge — start again
          from your client, or{' '}
          <Link to="/login" className="text-emerald-400 underline">
            sign in normally
          </Link>
          .
        </p>
      </div>
    )
  }

  if (isAuthLoading || isLoading) {
    return (
      <div className="min-h-screen app-sky flex flex-col items-center justify-center gap-4">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500" />
        <p className="text-harbor-text-secondary">Loading consent request…</p>
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen app-sky flex flex-col items-center justify-center gap-4 px-4">
        <BrandLogo size={48} src="/icon-256.png" className="rounded-2xl" />
        <h1 className="text-xl font-bold text-harbor-text">Authorize access</h1>
        <p className="max-w-md text-center text-sm text-harbor-text-secondary">
          Your sign-in expired (or this tab is in a different browser). Sign in, then come back to
          this tab and reload — the approval is still waiting.
        </p>
        <Link
          to="/login"
          className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700"
        >
          Sign in
        </Link>
      </div>
    )
  }

  return (
    <div className="min-h-screen app-sky flex flex-col justify-center px-4 py-10 sm:px-8">
      <div className="w-full max-w-md mx-auto">
        <div className="text-center mb-8">
          <BrandLogo size={64} src="/icon-256.png" className="mx-auto rounded-2xl mb-4" />
          <h1 className="text-2xl font-bold text-harbor-text">Authorize access</h1>
          <p className="text-harbor-text-secondary mt-2">
            An MCP client wants to access ContextHarbor on your behalf.
          </p>
        </div>
        {error && <p className="mb-4 text-sm text-red-400">{error}</p>}
        {consent ? (
          <dl className="mb-6 space-y-3 rounded-xl border border-harbor-border bg-harbor-surface p-5 text-sm">
            <div>
              <dt className="text-harbor-text-muted">Client</dt>
              <dd className="font-mono text-harbor-text">{consent.client_id}</dd>
            </div>
            <div>
              <dt className="text-harbor-text-muted">Scopes</dt>
              <dd className="font-mono text-harbor-text">
                {(consent.requested_scope ?? []).join(' ') || 'mcp:read'}
              </dd>
            </div>
            <div>
              <dt className="text-harbor-text-muted">Audience (this deployment&apos;s /mcp)</dt>
              <dd className="font-mono break-all text-harbor-text">
                {(consent.requested_audience ?? []).join(' ')}
              </dd>
            </div>
          </dl>
        ) : (
          !error && (
            <p className="mb-6 text-center text-sm text-harbor-text-secondary">
              Could not load the consent details — you can still deny below.
            </p>
          )
        )}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => void handleReject()}
            disabled={isWorking}
            className="flex-1 rounded-lg border border-harbor-border bg-harbor-surface px-4 py-3 text-sm font-medium text-harbor-text hover:bg-harbor-surface-raised disabled:opacity-50"
          >
            {isWorking ? 'Working…' : 'Deny'}
          </button>
          <button
            type="button"
            onClick={() => void handleAccept()}
            disabled={isWorking || (!consent && !error)}
            className="flex-1 rounded-lg bg-emerald-600 px-4 py-3 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {isWorking ? 'Working…' : 'Allow'}
          </button>
        </div>
        <p className="mt-6 text-center text-xs text-harbor-text-muted">
          Grants are audience-bound to this deployment&apos;s /mcp resource. Revoke anytime under
          Setup → MCP → OAuth grants.
        </p>
      </div>
    </div>
  )
}
